import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import { getMyPurchases } from "../services/purchase";
import { getMySubmissions } from "../services/exams";
import CourseCard from "../components/CourseCard";
import { motion } from "framer-motion";
import {
  Sparkles,
  TrendingUp,
  BookCheck,
  Clock,
  ArrowRight,
  Search,
  PlayCircle,
  Trophy,
  Zap,
  Globe,
  Monitor,
  BookOpen,
  Award,
  Users,
  Library,
  ExternalLink,
  Medal,
  Star,
  CalendarClock,
  GraduationCap,
  Target,
  Gamepad2,
  Puzzle,
  BadgeCheck,
  Brain,
  Flame,
  Bell,
  MailOpen,
  Mic
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';

// New UI Components
import { Card, Badge, StatCard, Skeleton, Button } from "../components/ui";
import ProgressRing from "../components/advanced/ProgressRing";

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    courses_enrolled: 0,
    overall_progress: 0,
    certificates_earned: 0,
    pending_assignments: 0,
    rank: "N/A",
  });
  const [leaderboard, setLeaderboard] = useState([]);

  // Gamification State
  const [gameStats, setGameStats] = useState({ stars: 0, xp: 0, level: 1 });
  const [badges, setBadges] = useState([]);

  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab");

  const getFilteredView = () => {
    if (activeTab === "my-courses") {
      return { title: "My Purchased Courses", subtitle: "View and open all your owned materials", courses: purchasedCourses, emptyMessage: "You haven't purchased any courses yet!" };
    }
    if (activeTab === "continue") {
      const inProgress = purchasedCourses.filter(c => (c.progress || 0) > 0);
      return { title: "Continue Learning", subtitle: "Resume your active studies exactly where you left off", courses: inProgress, emptyMessage: "You haven't started any courses yet. Go to 'My Courses' to begin your first lesson!" };
    }
    return null;
  };
  const filteredView = getFilteredView();

  useEffect(() => {
    if (activeTab === "notifications") fetchNotifications();
  }, [activeTab]);

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const res = await api.get("/notifications/");
      setNotifications(res.data || []);
    } catch (err) {} finally {
      setNotificationsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {}
  };

  const markAllAsRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success("All notifications cleared!");
    } catch (err) { toast.error("Unable to clear all."); }
  };

  const loadCourses = useCallback(async () => {
    try {
      const [res, purchaseRes, subsRes, booksRes, statsRes, leaderboardRes, gameRes, badgesRes] = await Promise.all([
        api.get("/courses").catch(() => ({ data: [] })),
        getMyPurchases().catch(() => ({ data: [] })),
        getMySubmissions().catch(() => ({ data: [] })),
        api.get("/books").catch(() => ({ data: [] })),
        api.get("/student/stats").catch(() => ({ data: null })),
        api.get("/student/leaderboard").catch(() => ({ data: [] })),
        api.get("/gamification/stats").catch(() => ({ data: { stars: 0, xp: 0, level: 1 } })),
        api.get("/gamification/badges").catch(() => ({ data: [] }))
      ]);
      setAllCourses(res.data);
      setPurchasedCourses(purchaseRes.data);
      setBooks(booksRes.data || []);
      if (statsRes.data) setStats(statsRes.data);
      if (leaderboardRes.data) setLeaderboard(leaderboardRes.data);
      setGameStats(gameRes.data);
      setBadges(badgesRes.data);
    } catch (err) { console.error(err); }
  }, []);

  const init = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    const decoded = jwtDecode(token);
    if (decoded.role === "admin" || decoded.role === "teacher" || decoded.role === "institute") {
      navigate(`/${decoded.role === 'institute' ? 'admin' : decoded.role}-dashboard`);
      return;
    }
    setUser(decoded);
    await loadCourses();
    setLoading(false);
  }, [loadCourses, navigate]);

  useEffect(() => { init(); }, [init]);

  const categories = Array.from(new Set(allCourses.map(c => c.category || "General"))).filter(Boolean);

  const getCategoryIcon = (cat) => {
    const c = cat.toLowerCase();
    if (c.includes("bank")) return { icon: <Globe />, color: "bg-blue-50 text-blue-600" };
    if (c.includes("ssc")) return { icon: <Zap />, color: "bg-amber-50 text-amber-600" };
    if (c.includes("rail")) return { icon: <Monitor />, color: "bg-red-50 text-red-600" };
    if (c.includes("upsc") || c.includes("civil")) return { icon: <BookOpen />, color: "bg-indigo-50 text-indigo-600" };
    if (c.includes("insur")) return { icon: <Award />, color: "bg-green-50 text-green-600" };
    return { icon: <Users />, color: "bg-violet-50 text-violet-600" };
  };

  if (loading || !user) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="w-full font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-6 lg:p-10 space-y-12 pb-20 min-w-0">
        {activeTab === "notifications" ? (
          <section className="space-y-8">
             <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
               <div>
                 <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                   <Bell size={32} className="text-indigo-600" /> Notifications Hub
                 </h1>
               </div>
               <div className="flex gap-3">
                 <Button variant="ghost" icon={<MailOpen size={16}/>} onClick={markAllAsRead} disabled={notifications.every(n => n.is_read)}>Clear All</Button>
                 <Button variant="secondary" onClick={() => navigate("/dashboard")}>← Back</Button>
               </div>
             </div>

             {notificationsLoading ? (
                <div className="py-20 flex justify-center"><Skeleton className="h-40 w-full rounded-3xl" /></div>
             ) : notifications.length === 0 ? (
                <Card variant="default" className="py-20 border-dashed text-center flex flex-col items-center">
                   <Bell size={64} className="text-indigo-200 mb-4 opacity-30" />
                   <p className="text-xl font-black text-slate-500">You're all caught up! 🎉</p>
                </Card>
             ) : (
                <div className="space-y-4 max-w-4xl">
                   {notifications.map(n => (
                      <Card key={n.id} variant={n.is_read ? "default" : "gradient-border"} className={`cursor-pointer transition-all ${n.is_read ? 'opacity-70' : ''}`} onClick={() => !n.is_read && markAsRead(n.id)}>
                        <Card.Body className="p-6 flex items-start gap-5">
                          <div className={`p-3 rounded-2xl ${n.type === 'assignment' ? 'bg-orange-50 text-orange-500' : n.type === 'exam' ? 'bg-rose-50 text-rose-500' : 'bg-blue-50 text-blue-500'}`}>
                             {n.type === 'assignment' ? <BookOpen size={20} /> : n.type === 'exam' ? <Award size={20} /> : <Bell size={20} />}
                          </div>
                          <div className="flex-1">
                             <div className="flex items-center justify-between">
                                <h4 className={`font-black text-lg ${n.is_read ? 'text-slate-700' : 'text-slate-900 dark:text-white'}`}>{n.title}</h4>
                                <span className="text-[10px] font-bold text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                             </div>
                             <p className="text-sm text-slate-500 mt-2">{n.message}</p>
                          </div>
                        </Card.Body>
                      </Card>
                   ))}
                </div>
             )}
          </section>
        ) : filteredView ? (
          <section className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">{filteredView.title}</h1>
                <p className="text-slate-500 mt-2 font-semibold text-lg">{filteredView.subtitle}</p>
              </div>
              <Button variant="secondary" onClick={() => navigate("/dashboard")}>← Back to Dashboard</Button>
            </div>

            {filteredView.courses.length === 0 ? (
              <Card variant="default" className="py-20 border-dashed flex flex-col items-center text-center">
                <PlayCircle size={64} className="text-indigo-200 mb-4" />
                <p className="text-xl font-black text-slate-500 max-w-md">{filteredView.emptyMessage}</p>
                {activeTab === "continue" && (
                  <Button variant="primary" className="mt-6" onClick={() => navigate("/dashboard?tab=my-courses")}>View Purchased Courses</Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredView.courses.map(c => <CourseCard key={c.id} course={c} isPurchased={true} onAction={() => navigate(`/course/${c.id}`)} />)}
              </div>
            )}
          </section>
        ) : (
          <>
            {/* WELCOME HEADER */}
            <section className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
                <Badge variant="primary" className="mb-4">
                  <Sparkles size={12} className="inline mr-1" /> Welcome back, Explorer
                </Badge>
                <h1 className="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white leading-tight tracking-tight">
                  Hello, <span className="text-indigo-600">{user.email.split('@')[0]}</span>
                </h1>
                <p className="text-slate-500 mt-4 text-lg font-medium">
                  Your overall master progress stands at <span className="font-black text-slate-900 dark:text-white">{stats.overall_progress}%</span>. Keep sharpening your skills!
                </p>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05 }} className="hidden sm:block">
                <Card variant="glass" className="p-6 flex items-center gap-6 relative overflow-hidden bg-gradient-to-br from-indigo-900 to-violet-900 border-none shadow-2xl">
                  <div className="absolute -right-4 -top-4 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                  <ProgressRing progress={stats.overall_progress} size={80} strokeWidth={6} color="#818cf8" trackColor="rgba(255,255,255,0.1)" />
                  <div className="text-white relative z-10">
                    <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mb-1">Academy Mastery</p>
                    <p className="text-3xl font-black tracking-tight">{stats.overall_progress}%</p>
                  </div>
                </Card>
              </motion.div>
            </section>

            {/* DASHBOARD ANALYTICS GRID */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
              <StatCard title="Courses Enrolled" value={stats.courses_enrolled} icon={BookOpen} variant="glass" />
              <StatCard title="Pending Assignments" value={stats.pending_assignments} icon={CalendarClock} variant="gradient" gradient="from-rose-500 to-orange-500" />
              <StatCard title="Certificates Earned" value={stats.certificates_earned} icon={Award} variant="glass" />
              <StatCard title="Global Rank" value={stats.rank === "N/A" ? "..." : `#${stats.rank}`} icon={Trophy} variant="gradient" gradient="from-amber-400 to-amber-600" />
            </section>

            {/* CONTINUE LEARNING - MOVED TO TOP */}
            {purchasedCourses.length > 0 && (
              <section className="space-y-6 mt-12">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Your Courses</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {purchasedCourses.map(c => <CourseCard key={c.id} course={c} isPurchased={true} onAction={() => navigate(`/course/${c.id}`)} />)}
                </div>
              </section>
            )}

            {/* AI TOOLS */}
            <section className="space-y-6 mt-12">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Smart Learning & Quests</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: "Speak to Voice AI", desc: "Immersive talking tutor bot", icon: Mic, route: "/voice-tutor", badge: "AI Voice", color: "from-indigo-600 to-violet-600" },
                  { label: "Daily Challenge", desc: "Maintain streak, win stars!", icon: Flame, route: "/daily-challenges", badge: "Streak", color: "from-amber-500 to-orange-500" },
                  { label: "Friend Compete", desc: "Battle for top ranking", icon: Users, route: "/friend-competition", badge: "Versus", color: "from-rose-500 to-pink-600" },
                  { label: "Smart Revision", desc: "Conquer weak topics", icon: Brain, route: "/ai-revision", badge: "Brain Boost", color: "from-sky-500 to-blue-600" },
                ].map((tile, idx) => (
                  <motion.div key={idx} whileHover={{ y: -6, scale: 1.02 }} onClick={() => navigate(tile.route)}>
                    <Card variant="default" className="h-full cursor-pointer relative overflow-hidden group hover:border-indigo-200 transition-all">
                      <Card.Body className="p-6 flex flex-col justify-between h-full">
                        <Badge variant="primary" className="absolute top-4 right-4 text-[9px] bg-slate-100 dark:bg-slate-800 text-slate-500 border-none group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-colors">
                          {tile.badge}
                        </Badge>
                        <div>
                          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${tile.color} text-white shadow-lg flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                            <tile.icon size={22} />
                          </div>
                          <h4 className="text-lg font-black text-slate-900 dark:text-white tracking-tight leading-tight">{tile.label}</h4>
                          <p className="text-slate-400 text-xs font-medium mt-1.5">{tile.desc}</p>
                        </div>
                        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-indigo-600 transition-colors">
                          Launch Platform <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* LIBRARY */}
            {books.length > 0 && (
              <section className="space-y-6 mt-12">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Recommended Books</h2>
                <div className="flex gap-6 overflow-x-auto pb-6 no-scrollbar">
                  {books.map((book) => (
                    <motion.div key={book.id} whileHover={{ y: -5 }} className="min-w-[280px]">
                      <Card variant="glass" className="overflow-hidden group">
                        <div className="aspect-[3/4] bg-slate-200 dark:bg-slate-800 m-4 rounded-2xl overflow-hidden relative">
                          {book.thumbnail && <img src={book.thumbnail} alt={book.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />}
                        </div>
                        <Card.Body className="px-4 pb-4 pt-0">
                          <Badge variant="primary" className="mb-2 text-[10px] border-none bg-indigo-50">{book.category}</Badge>
                          <h4 className="text-lg font-black text-slate-900 dark:text-white line-clamp-1">{book.title}</h4>
                          <p className="text-xs font-bold text-slate-400 mb-4">By {book.author}</p>
                          <Button variant="outline" className="w-full" onClick={() => navigate(`/read-book/${book.id}`)} icon={<ExternalLink size={14} />}>
                            Read Now
                          </Button>
                        </Card.Body>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* BROWSE & LEADERBOARD */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
              <div className="lg:col-span-2 space-y-6">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Browse Categories</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {categories.slice(0, 6).map((cat, i) => {
                    const style = getCategoryIcon(cat);
                    return (
                      <motion.div key={i} whileHover={{ scale: 1.02 }}>
                        <Card variant="default" className="cursor-pointer hover:border-indigo-200 group" onClick={() => navigate(`/category/${cat}`)}>
                          <Card.Body className="p-6 flex flex-col items-start gap-4">
                            <div className={`p-3.5 rounded-2xl ${style.color} group-hover:scale-110 transition-transform`}>
                              {React.cloneElement(style.icon, { size: 18 })}
                            </div>
                            <span className="font-black text-slate-900 dark:text-slate-200 text-xs uppercase tracking-widest line-clamp-1">{cat}</span>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              <Card variant="gradient-border" className="flex flex-col h-full">
                <Card.Header 
                  title={<span className="flex items-center gap-2"><Medal className="text-amber-500" size={20} /> Global Leaderboard</span>} 
                />
                <Card.Body className="flex-1 overflow-y-auto">
                  <div className="space-y-3">
                    {leaderboard.map((std, index) => (
                      <div key={std.id} className={`flex items-center gap-3 p-3 rounded-2xl transition-all ${std.id === user.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs ${index === 0 ? 'bg-amber-100 text-amber-700' : index === 1 ? 'bg-slate-200 text-slate-700' : index === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-50 text-slate-400 dark:bg-slate-800'}`}>
                          {index + 1}
                        </div>
                        <div className="w-9 h-9 bg-indigo-100 dark:bg-slate-700 rounded-xl overflow-hidden shrink-0 shadow-sm flex items-center justify-center text-xs font-bold text-indigo-600 dark:text-indigo-300 uppercase">
                          {std.profile_picture ? <img src={std.profile_picture} alt="" className="w-full h-full object-cover" /> : std.name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{std.name} {std.id === user.id && <span className="text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded-full ml-1 font-black uppercase">Me</span>}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-black text-slate-900 dark:text-white">{std.score}</p>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">pts</p>
                        </div>
                      </div>
                    ))}
                    {leaderboard.length === 0 && <div className="py-12 text-center text-slate-400 text-sm font-bold">No rankings yet</div>}
                  </div>
                </Card.Body>
              </Card>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
